<template>
  <div>
    <b-modal
      :id="MODAL_ID"
      title="Batch Translate Images"
      size="xl"
      hide-footer
      no-close-on-backdrop
      @show="onModalOpen"
      @hide="onModalHide"
    >
      <div class="position-relative modal-content-wrapper">
        <!-- Step 1: Select Templates -->
        <div v-if="currentStep === 1">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="mb-0 font-weight-bold">Select Templates to Translate</label>
            <div>
              <button
                class="btn btn-sm btn-outline-secondary mr-1"
                @click="selectAllTemplates"
              >
                Select All
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="deselectAllTemplates"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div
            v-if="allTemplates.length === 0"
            class="text-muted text-center py-4"
          >
            No templates available.
          </div>
          <div
            v-else
            class="template-selection-list"
          >
            <div
              v-for="tmpl in allTemplates"
              :key="tmpl.instanceId"
              class="template-selection-item"
              :class="{ 'selected': selectedTemplateIds.includes(tmpl.instanceId) }"
              @click="toggleTemplate(tmpl.instanceId)"
            >
              <b-form-checkbox
                :checked="selectedTemplateIds.includes(tmpl.instanceId)"
                class="mr-2"
                @change="toggleTemplate(tmpl.instanceId)"
                @click.native.stop
              />
              <div
                v-if="tmpl.canvasThumbnail"
                class="template-thumb mr-2"
              >
                <img
                  :src="tmpl.canvasThumbnail"
                  alt=""
                >
              </div>
              <div
                v-else
                class="template-thumb-placeholder mr-2"
              />
              <span class="template-name">{{ tmpl.name || `Template ${tmpl.instanceId}` }}</span>
              <span class="ml-auto text-muted small">{{ tmpl.imageCount }} image(s)</span>
            </div>
          </div>
          <div class="mt-3 d-flex justify-content-end">
            <button
              class="btn btn-primary"
              :disabled="selectedTemplateIds.length === 0"
              @click="goToStep2"
            >
              Next ({{ selectedTemplateIds.length }} selected)
            </button>
          </div>
        </div>

        <!-- Step 2: Configure Images & Languages -->
        <div v-if="currentStep === 2">
          <button
            class="btn btn-sm btn-outline-secondary mb-3"
            @click="currentStep = 1"
          >
            <i class="bi bi-arrow-left mr-1" /> Back
          </button>

          <div class="mb-3">
            <label class="font-weight-bold">Image Selection per Template</label>
            <div
              v-for="tmpl in selectedTemplates"
              :key="tmpl.instanceId"
              class="template-image-config mb-2 p-2 border rounded"
            >
              <div>
                <span class="font-weight-bold d-block mb-2">{{ tmpl.name || `Template ${tmpl.instanceId}` }}</span>
                <div class="image-checkbox-list">
                  <b-form-checkbox
                    v-for="option in tmpl.imageOptions"
                    :key="option.value"
                    :checked="isImageSelected(tmpl.instanceId, option.value)"
                    class="image-checkbox-item"
                    @change="toggleImageSelection(tmpl.instanceId, option.value, $event)"
                  >
                    <div class="d-flex align-items-center">
                      <img
                        v-if="option.thumbnail"
                        :src="option.thumbnail"
                        class="image-thumbnail mr-2"
                        alt=""
                      >
                      <span
                        v-else
                        class="image-thumbnail-placeholder mr-2"
                      />
                      <span>{{ option.text }}</span>
                    </div>
                  </b-form-checkbox>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group">
            <label class="font-weight-bold">Select Languages</label>
            <Multiselect
              v-model="selectedTargetLanguages"
              :options="availableLanguages"
              :multiple="true"
              :close-on-select="false"
              :taggable="false"
              placeholder="Select language(s)..."
              class="multiselect-language"
            />
          </div>

          <div class="form-group">
            <b-form-checkbox v-model="autoDetectTextBlocks">
              Auto-detect text blocks (recommended)
            </b-form-checkbox>
          </div>

          <div class="d-flex justify-content-end">
            <button
              class="btn btn-primary"
              :disabled="!canTranslate || isTranslating"
              @click="handleBatchTranslate"
            >
              <span
                v-if="isTranslating"
                class="spinner-border spinner-border-sm mr-1"
                role="status"
                aria-hidden="true"
              />
              {{ isTranslating ? 'Translating...' : 'Translate' }}
            </button>
          </div>
        </div>

        <!-- Step 3: Results -->
        <div v-if="currentStep === 3">
          <button
            class="btn btn-sm btn-outline-secondary mb-3"
            :disabled="isTranslating"
            @click="currentStep = 2"
          >
            <i class="bi bi-arrow-left mr-1" /> Back
          </button>

          <div class="results-container">
            <div
              v-for="(tmpl, resultIndex) in translationResults"
              :key="resultIndex"
              class="result-template-row mb-4"
            >
              <h6 class="font-weight-bold mb-2">
                {{ tmpl.name || `Template ${tmpl.instanceId}` }}
              </h6>
              <div class="translated-images-grid">
                <div
                  v-for="(item, langIdx) in tmpl.languages"
                  :key="langIdx"
                  class="translated-image-item"
                >
                  <span class="translated-image-label">{{ item.language }}</span>
                  <div class="image-preview-wrapper position-relative">
                    <!-- Loading State -->
                    <div
                      v-if="item.isLoading"
                      class="d-flex flex-column justify-content-center align-items-center bg-light"
                      style="min-height: 150px; width: 100%; border-radius: 4px;"
                    >
                      <div
                        class="spinner-border text-primary"
                        role="status"
                      >
                        <span class="sr-only">Loading...</span>
                      </div>
                      <div class="mt-2 text-muted small">
                        Translating...
                      </div>
                    </div>

                    <!-- Error State -->
                    <div
                      v-else-if="item.error"
                      class="d-flex flex-column justify-content-center align-items-center bg-light text-danger"
                      style="min-height: 150px; width: 100%; border-radius: 4px;"
                    >
                      <i
                        class="bi bi-exclamation-triangle mb-2"
                        style="font-size: 1.5rem;"
                      />
                      <span class="mb-2 small">Failed</span>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        @click.stop="retryTranslation(resultIndex, langIdx)"
                      >
                        <i class="bi bi-arrow-clockwise mr-1" /> Retry
                      </button>
                    </div>

                    <!-- Success State -->
                    <template v-else-if="item.url">
                      <img
                        class="img img-fluid modal-preview-image"
                        :src="item.url"
                      >
                      <div class="image-actions-container">
                        <button
                          class="retry-btn"
                          title="Retry translation"
                          @click.stop="retryTranslation(resultIndex, langIdx)"
                        >
                          <i class="bi bi-arrow-clockwise" />
                        </button>
                        <button
                          class="download-btn"
                          title="Download image"
                          @click.stop="downloadImage(item.url, `${tmpl.name}-${item.language}`)"
                        >
                          <i class="bi bi-download" />
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3 d-flex justify-content-between">
            <b-dropdown
              text="Add all to new templates"
              variant="primary"
              :disabled="isCreatingTemplates || !hasAnySuccessfulTranslation"
            >
              <b-dropdown-item @click="handleAddAllToNewTemplates('original')">
                Original size
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="handleAddAllToNewTemplates(1500, 1500)">
                1500x1500
              </b-dropdown-item>
              <b-dropdown-item @click="handleAddAllToNewTemplates(2000, 2000)">
                2000x2000
              </b-dropdown-item>
              <b-dropdown-item @click="handleAddAllToNewTemplates(2500, 2500)">
                2500x2500
              </b-dropdown-item>
              <b-dropdown-item @click="handleAddAllToNewTemplates(3000, 3000)">
                3000x3000
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="handleAddAllToNewTemplates('custom')">
                Custom...
              </b-dropdown-item>
            </b-dropdown>

            <button
              class="btn btn-outline-primary"
              :disabled="!hasAnySuccessfulTranslation"
              @click="downloadAll"
            >
              <i class="bi bi-download mr-1" /> Download All
            </button>
          </div>

          <div
            v-if="isCreatingTemplates"
            class="mt-2 text-center"
          >
            <span
              class="spinner-border spinner-border-sm mr-1"
              role="status"
            />
            Creating templates... {{ templatesCreatedCount }}/{{ totalTemplatesToCreate }}
          </div>
        </div>

        <!-- Loading overlay for text block detection -->
        <div
          v-if="isDetectingTextBlocks"
          class="loading-overlay"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="sr-only">Loading...</span>
          </div>
          <div class="mt-2 font-weight-bold">
            Detecting text blocks... ({{ textBlocksDetectedCount }}/{{ textBlocksTotal }})
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2";
import API_TEMPLATE from "@frontend/services/api/template";
import {
  translateTemplateImageEventBroker,
  EVENTS as MODAL_EVENTS,
} from "@frontend/group/modules/translate-template-image/event-broker";
import {
  FIELD_TYPE_BACKGROND_THEME_IMAGE,
  FIELD_TYPE_PRODUCT_IMAGE,
  FIELD_TYPE_STATIC_IMAGE,
  FIELD_TYPE_UPLOAD_IMAGE,
  FIELD_TYPE_IMAGE_FROM_BACKGROUND,
} from "@frontend/constants/type-fields-of-template";
import {
  DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES,
} from "@frontend/group/modules/additional-fields";
import { deleteURLParameters, setURLParameters } from "@frontend/utils";
import Multiselect from "vue-multiselect";
import 'vue-multiselect/dist/vue-multiselect.min.css';

const MODAL_ID = "batch-translate-template-image-modal";
const MAX_CONCURRENT = 4;

const IMAGE_FIELD_TYPES = [
  FIELD_TYPE_BACKGROND_THEME_IMAGE,
  FIELD_TYPE_PRODUCT_IMAGE,
  FIELD_TYPE_STATIC_IMAGE,
  FIELD_TYPE_UPLOAD_IMAGE,
  FIELD_TYPE_IMAGE_FROM_BACKGROUND,
];

export default {
  name: "BatchTranslateTemplateImageModal",
  components: {
    Multiselect,
  },
  data() {
    return {
      MODAL_ID,
      currentStep: 1,
      allTemplates: [],
      selectedTemplateIds: [],
      templateImageSelections: {},
      availableLanguages: [
        "Arabic", "Bengali", "Cantonese", "Czech", "Dutch", "English",
        "French-CA", "French-FR", "German", "Greek", "Hebrew",
        "Hindi", "Italian", "Japanese", "Korean", "Polish",
        "Portuguese", "Russian", "Simplified Chinese", "Slovak",
        "Spanish (LatAm)", "Spanish (Spain/EU)", "Swedish",
        "Thai", "Traditional Chinese", "Turkish", "Vietnamese",
      ],
      selectedTargetLanguages: [],
      autoDetectTextBlocks: true,
      isTranslating: false,
      isCreatingTemplates: false,
      isDetectingTextBlocks: false,
      textBlocksDetectedCount: 0,
      textBlocksTotal: 0,
      translationResults: [],
      templateTextBlocks: {},
      layoutId: 0,
      imageTranslationModel: "gemini_nano_banana_pro_image_generate",
      imageTranslationQuality: "2k",
      templatesCreatedCount: 0,
      totalTemplatesToCreate: 0,
    };
  },
  computed: {
    ...mapGetters("layout", ["selectedInstance"]),
    selectedTemplates() {
      return this.allTemplates.filter(t => this.selectedTemplateIds.includes(t.instanceId));
    },
    canTranslate() {
      if (this.selectedTargetLanguages.length === 0) return false;
      return this.selectedTemplates.every(t => {
        const sel = this.templateImageSelections[t.instanceId];
        return Array.isArray(sel) && sel.length > 0;
      });
    },
    hasAnySuccessfulTranslation() {
      return this.translationResults.some(r =>
        r.languages.some(l => l.url && !l.isLoading && !l.error)
      );
    },
  },
  mounted() {
    translateTemplateImageEventBroker.on(
      MODAL_EVENTS.BATCH_INIT,
      this.init.bind(this),
    );
  },
  methods: {
    async callOpenAI(settings) {
      const { data } = await axios.post(`/call-open-ai`, settings);
      return data;
    },

    init({ templates }) {
      this.currentStep = 1;
      this.selectedTemplateIds = [];
      this.templateImageSelections = {};
      this.translationResults = [];
      this.templateTextBlocks = {};
      this.selectedTargetLanguages = [];

      const url = window.location.href;
      const match = url.match(/\/group\/(\d+)\//);
      if (match) {
        this.layoutId = parseInt(match[1], 10);
      }

      this.imageTranslationModel = "gemini_nano_banana_pro_image_generate";
      this.imageTranslationQuality = "4k";

      this.allTemplates = templates.map(tmpl => {
        const imageOptions = [];
        const fields = tmpl.fields;

        for (let i = 0; i < fields.length; i++) {
          const field = fields[i];
          const isImageField = IMAGE_FIELD_TYPES.includes(field.type);
          const urlString = field.url?.href || field.url?.toString?.() || field.url;
          const isDefaultImage = urlString === DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images;

          if (isImageField && !isDefaultImage) {
            const thumbnailUrl = field.thumbnail?.href || field.thumbnail?.toString?.() || field.thumbnail;
            imageOptions.push({
              text: field.name,
              value: i,
              thumbnail: typeof thumbnailUrl === "string" ? thumbnailUrl : null,
              fieldType: field.type,
            });
          }
        }

        // Generate canvas thumbnail
        let canvasThumbnail = null;
        try {
          if (tmpl.preview?.canvas) {
            canvasThumbnail = tmpl.preview.canvas.toDataURL({ format: 'png', multiplier: 0.15 });
          }
        } catch (e) {
          // CORS or other error
        }

        const templateName = tmpl.preview?.template?.name || tmpl.preview?.data?.name || null;

        return {
          instanceId: tmpl.instanceId,
          name: templateName,
          fields: tmpl.fields,
          objects: tmpl.objects,
          preview: tmpl.preview,
          imageOptions,
          imageCount: imageOptions.length,
          canvasThumbnail,
        };
      });

      this.$bvModal.show(MODAL_ID);
    },

    onModalOpen() {
      // Reset
    },

    onModalHide() {
      this.isTranslating = false;
      this.isCreatingTemplates = false;
    },

    selectAllTemplates() {
      this.selectedTemplateIds = this.allTemplates.map(t => t.instanceId);
    },

    deselectAllTemplates() {
      this.selectedTemplateIds = [];
    },

    toggleTemplate(id) {
      const idx = this.selectedTemplateIds.indexOf(id);
      if (idx >= 0) {
        this.selectedTemplateIds.splice(idx, 1);
      } else {
        this.selectedTemplateIds.push(id);
      }
    },

    isImageSelected(instanceId, value) {
      const sel = this.templateImageSelections[instanceId];
      return Array.isArray(sel) && sel.includes(value);
    },

    toggleImageSelection(instanceId, value, checked) {
      let sel = this.templateImageSelections[instanceId];
      if (!Array.isArray(sel)) sel = [];

      if (checked) {
        if (!sel.includes(value)) sel = [...sel, value];
      } else {
        sel = sel.filter(v => v !== value);
      }
      this.$set(this.templateImageSelections, instanceId, sel);
    },

    goToStep2() {
      // Auto-select all images for templates
      this.selectedTemplates.forEach(tmpl => {
        if (!this.templateImageSelections[tmpl.instanceId] || !Array.isArray(this.templateImageSelections[tmpl.instanceId])) {
          this.$set(this.templateImageSelections, tmpl.instanceId, tmpl.imageOptions.map(o => o.value));
        }
      });
      this.currentStep = 2;
    },

    getImageUrlFromField(tmpl, fieldIdx) {
      const targetElement = tmpl.objects[fieldIdx]?.target;
      if (!targetElement) return null;

      const isImage = targetElement.type === "image" ||
        (targetElement.id && (targetElement.id.includes("image_") || targetElement.id.includes("product_image")));

      if (!isImage) return null;
      if (targetElement?.getSrc()?.includes(";base64,")) return null;

      let originalSrc = deleteURLParameters(targetElement.getSrc(), ["lightweight", "full_resolution"]);
      originalSrc = setURLParameters(originalSrc, { full_resolution: true });

      if (originalSrc?.includes(DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES.additional_background_images)) {
        return null;
      }

      return originalSrc;
    },

    getSelectedImageUrls(tmpl) {
      const selectedIndices = this.templateImageSelections[tmpl.instanceId];
      if (!Array.isArray(selectedIndices) || selectedIndices.length === 0) return [];

      const urls = [];
      for (const fieldIdx of selectedIndices) {
        const url = this.getImageUrlFromField(tmpl, fieldIdx);
        if (url) {
          const option = tmpl.imageOptions.find(o => o.value === fieldIdx);
          urls.push({ url, name: option?.text || `Image ${fieldIdx}` });
        }
      }
      return urls;
    },

    async fetchImageBlob(imageUrl) {
      let url = imageUrl;
      if (url.startsWith("/")) {
        url = window.location.origin + url;
      }
      const response = await fetch(url);
      return response.blob();
    },

    async fetchTextBlocksForImage(imageUrl) {
      let url = imageUrl;
      if (url.startsWith("/")) {
        url = window.location.origin + url;
      }

      const imageResponse = await fetch(url);
      const blob = await imageResponse.blob();

      const base64Promise = new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const base64Image = await base64Promise;

      const payload = {
        model: "gpt-4o",
        messages: [
          {
            role: "user",
            content: [
              {
                type: "text",
                text: "Extract all visible text blocks from this image. A text block can be a simple line or a paragraph with multiple lines. Return a JSON object with a key 'blocks' containing an array of strings."
              },
              {
                type: "image_url",
                image_url: { url: base64Image }
              }
            ]
          }
        ],
        response_format: {
          name: "extracted_blocks",
          schema: {
            type: "object",
            properties: {
              blocks: {
                type: "array",
                items: { type: "string" }
              }
            },
            required: ["blocks"],
            additionalProperties: false
          },
          strict: true
        }
      };

      const responseData = await this.callOpenAI(payload);

      let contentString = null;
      if (responseData?.content) {
        contentString = responseData.content;
      } else if (responseData?.choices?.length > 0) {
        contentString = responseData.choices[0].message.content;
      }

      if (contentString) {
        try {
          const contentJson = JSON.parse(contentString);
          if (contentJson && Array.isArray(contentJson.blocks)) {
            const filteredBlocks = contentJson.blocks.filter(block => block && block.length > 2);
            return [...new Set(filteredBlocks)];
          }
        } catch (e) {
          console.error("Error parsing content JSON:", e);
        }
      }

      return [];
    },

    async handleBatchTranslate() {
      if (this.isTranslating) return;

      const templates = this.selectedTemplates;
      const languages = this.selectedTargetLanguages;

      // Validate all templates have images selected
      for (const tmpl of templates) {
        const imageUrls = this.getSelectedImageUrls(tmpl);
        if (imageUrls.length === 0) {
          toastr.error(`No valid image found for template "${tmpl.name || tmpl.instanceId}".`);
          return;
        }
      }

      this.isTranslating = true;

      try {
        // Build per-template image info for results and tasks
        const templateImageInfo = templates.map(tmpl => ({
          tmpl,
          images: this.getSelectedImageUrls(tmpl),
        }));

        // Detect text blocks if auto-detect is on (per image)
        if (this.autoDetectTextBlocks) {
          this.isDetectingTextBlocks = true;
          const allImages = templateImageInfo.flatMap(t => t.images);
          this.textBlocksDetectedCount = 0;
          this.textBlocksTotal = allImages.length;

          for (const info of templateImageInfo) {
            for (const img of info.images) {
              const key = `${info.tmpl.instanceId}__${img.name}`;
              try {
                const blocks = await this.fetchTextBlocksForImage(img.url);
                this.$set(this.templateTextBlocks, key, blocks);
              } catch (e) {
                console.error(`Failed to detect text blocks for ${key}:`, e);
                this.$set(this.templateTextBlocks, key, []);
              }
              this.textBlocksDetectedCount++;
            }
          }

          this.isDetectingTextBlocks = false;
        }

        // Initialize results: one result row per template+image combination
        this.translationResults = [];
        let resultIdx = 0;
        const taskMap = []; // maps resultIdx to { tmpl, imageUrl, imageName }

        for (const info of templateImageInfo) {
          for (const img of info.images) {
            const label = info.images.length > 1
              ? `${info.tmpl.name || `Template ${info.tmpl.instanceId}`} - ${img.name}`
              : (info.tmpl.name || `Template ${info.tmpl.instanceId}`);

            this.translationResults.push({
              instanceId: info.tmpl.instanceId,
              name: label,
              imageName: img.name,
              preview: info.tmpl.preview,
              languages: languages.map(lang => ({
                language: lang,
                url: null,
                isLoading: true,
                error: false,
              })),
            });

            taskMap.push({ resultIdx, tmpl: info.tmpl, imageUrl: img.url, imageName: img.name });
            resultIdx++;
          }
        }

        this.currentStep = 3;

        // Build all translation tasks
        const tasks = [];
        for (const entry of taskMap) {
          const imageBlob = await this.fetchImageBlob(entry.imageUrl);

          for (let lIdx = 0; lIdx < languages.length; lIdx++) {
            tasks.push({
              templateIdx: entry.resultIdx,
              langIdx: lIdx,
              instanceId: entry.tmpl.instanceId,
              imageName: entry.imageName,
              language: languages[lIdx],
              imageBlob,
            });
          }
        }

        // Process with concurrency limit
        await this.processWithConcurrency(tasks, MAX_CONCURRENT);

      } catch (err) {
        console.error("handleBatchTranslate error:", err);
        toastr.error("Service temporarily unavailable.");
      } finally {
        this.isTranslating = false;
      }
    },

    async processWithConcurrency(tasks, maxConcurrent) {
      let index = 0;

      const runNext = async () => {
        while (index < tasks.length) {
          const task = tasks[index++];
          await this.processOneTranslation(task);
        }
      };

      const workers = [];
      for (let i = 0; i < Math.min(maxConcurrent, tasks.length); i++) {
        workers.push(runNext());
      }

      await Promise.all(workers);
    },

    async processOneTranslation(task) {
      const { templateIdx, langIdx, instanceId, imageName, language, imageBlob } = task;
      const resultEntry = this.translationResults[templateIdx];
      if (!resultEntry) return;

      const item = resultEntry.languages[langIdx];

      try {
        const doNotTranslateSymbolsRule = `Do not translate symbols like "+", "-", "*", "/", "\u2116", "\u00b0", etc. For example, do not translate "25+ minutes" as "m\u00e1s de 25 minutos". Do not change the color of the font and do not make any other edits or changes.`;

        let prompt = `Edit this image and replace all the text with its translation to ${language}. Translate all the text including logos, brand names, product names, etc. ${doNotTranslateSymbolsRule}`;

        const textBlockKey = `${instanceId}__${imageName}`;
        const textBlocks = this.templateTextBlocks[textBlockKey];
        if (this.autoDetectTextBlocks && textBlocks && textBlocks.length > 0) {
          const blocksToTranslate = textBlocks.map(block => `"${block}"`).join(', ');
          prompt = `Edit this image and replace only the following text blocks with their translation to ${language}: ${blocksToTranslate}. A text block can be a simple line or a paragraph with multiple lines. Do NOT translate any other text not listed here. ${doNotTranslateSymbolsRule}`;
        }

        const formData = new FormData();
        formData.append("layout_id", this.layoutId);
        formData.append("layout_template_id", instanceId);
        formData.append("function", this.imageTranslationModel);
        formData.append("max_quality", this.imageTranslationQuality);
        formData.append("prompt", prompt);
        formData.append("number_of_images", 1);

        const file = new File([imageBlob], "input.png", { type: imageBlob.type });
        formData.append("reference_images[]", file);

        const generateRes = await axios.post(
          "/banner/groups/generate_image",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (!generateRes?.data || generateRes.data.error) {
          throw new Error(`Failed to translate to ${language}.`);
        }

        const jobUUID = generateRes.data.uuid;
        if (!jobUUID) {
          throw new Error(`Invalid job id for ${language}.`);
        }

        const checkUrl = `/banner/groups/check_image_generation_job/${jobUUID}`;
        let finalResult = null;
        let attempts = 0;
        const maxAttempts = 60;

        while (attempts < maxAttempts) {
          const checkRes = await axios.get(checkUrl);

          if (checkRes.data.done || (checkRes.data.image_urls && checkRes.data.image_urls.length > 0)) {
            finalResult = checkRes.data;
            break;
          }

          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }

        if (finalResult?.image_urls?.length > 0) {
          this.$set(resultEntry.languages, langIdx, {
            ...item,
            url: finalResult.image_urls[0],
            isLoading: false,
            error: false,
          });
        } else {
          throw new Error(`Translation to ${language} timed out.`);
        }
      } catch (e) {
        console.error(`Error translating ${instanceId} to ${language}:`, e);
        this.$set(resultEntry.languages, langIdx, {
          ...item,
          isLoading: false,
          error: true,
        });
      }
    },

    async retryTranslation(resultIndex, langIdx) {
      const resultEntry = this.translationResults[resultIndex];
      if (!resultEntry) return;

      const item = resultEntry.languages[langIdx];
      if (!item || item.isLoading) return;

      const tmpl = this.allTemplates.find(t => t.instanceId === resultEntry.instanceId);
      if (!tmpl) return;

      // Find the specific image URL for this result entry
      const images = this.getSelectedImageUrls(tmpl);
      const img = images.find(i => resultEntry.name.includes(i.name)) || images[0];
      if (!img) {
        toastr.error("No source image available.");
        return;
      }

      this.$set(resultEntry.languages, langIdx, { ...item, isLoading: true, error: false });

      try {
        const imageBlob = await this.fetchImageBlob(img.url);

        await this.processOneTranslation({
          templateIdx: resultIndex,
          langIdx,
          instanceId: resultEntry.instanceId,
          imageName: resultEntry.imageName || img.name,
          language: item.language,
          imageBlob,
        });
      } catch (e) {
        console.error("Retry failed:", e);
        this.$set(resultEntry.languages, langIdx, { ...item, isLoading: false, error: true });
      }
    },

    async handleAddAllToNewTemplates(widthOrOption, height = null) {
      if (!this.hasAnySuccessfulTranslation) return;

      if (widthOrOption === 'custom') {
        const result = await this.showCustomSizeDialog();
        if (result.isConfirmed && result.value) {
          await this.createAllTemplatesWithSize(result.value.width, result.value.height);
        }
        return;
      }

      if (widthOrOption === 'original') {
        await this.createAllTemplatesWithSize(null, null, true);
        return;
      }

      if (typeof widthOrOption === "number" && typeof height === "number") {
        await this.createAllTemplatesWithSize(widthOrOption, height);
      }
    },

    showCustomSizeDialog() {
      return Swal.fire({
        title: "Enter custom size",
        html:
          '<div class="form-group">' +
          '<label for="batch-custom-width">Width (px)</label>' +
          '<input id="batch-custom-width" type="number" class="form-control" value="2000" min="100" max="10000">' +
          "</div>" +
          '<div class="form-group">' +
          '<label for="batch-custom-height">Height (px)</label>' +
          '<input id="batch-custom-height" type="number" class="form-control" value="2000" min="100" max="10000">' +
          "</div>",
        showCancelButton: true,
        confirmButtonText: "Create",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          const width = document.getElementById("batch-custom-width").value;
          const height = document.getElementById("batch-custom-height").value;
          if (!width || !height || width < 100 || height < 100 || width > 10000 || height > 10000) {
            Swal.showValidationMessage("Please enter valid dimensions (100-10000px)");
            return false;
          }
          return { width: parseInt(width), height: parseInt(height) };
        },
      });
    },

    async createAllTemplatesWithSize(width, height, useOriginal = false) {
      this.isCreatingTemplates = true;
      this.templatesCreatedCount = 0;

      const grid = $("#template-group-preview");
      const customerId = grid.attr("data-customer-id");

      // Collect all successful translations
      const toCreate = [];
      for (const result of this.translationResults) {
        const tmpl = this.allTemplates.find(t => t.instanceId === result.instanceId);

        let w = width;
        let h = height;
        if (useOriginal && tmpl?.preview?.template) {
          w = parseInt(tmpl.preview.template.width);
          h = parseInt(tmpl.preview.template.height);
        }

        for (const langItem of result.languages) {
          if (langItem.url && !langItem.isLoading && !langItem.error) {
            toCreate.push({
              instanceId: result.instanceId,
              imageUrl: langItem.url,
              language: langItem.language,
              width: w,
              height: h,
            });
          }
        }
      }

      this.totalTemplatesToCreate = toCreate.length;

      try {
        for (const item of toCreate) {
          let imageUrl = item.imageUrl;

          // Convert HTTP URL to server-side blob
          if (imageUrl.startsWith("http")) {
            const formData = new FormData();
            formData.append("img_url", imageUrl);
            formData.append("return_blob", 1);

            const response = await axios({
              method: "post",
              url: `/banner/${customerId}/group/${this.layoutId}/template/${item.instanceId}/upload_generated_background`,
              data: formData,
            });
            imageUrl = response.data;
          }

          try {
            await API_TEMPLATE.createTemplateWithSize(
              customerId,
              this.layoutId,
              imageUrl,
              item.width,
              item.height,
              [],
              "fit",
              item.language,
            );
            this.templatesCreatedCount++;
          } catch (error) {
            if (error.response && error.response.status === 422) {
              await new Promise(resolve => setTimeout(resolve, 3000));
              await API_TEMPLATE.createTemplateWithSize(
                customerId,
                this.layoutId,
                imageUrl,
                item.width,
                item.height,
                [],
                "fit",
                item.language,
              );
              this.templatesCreatedCount++;
            } else {
              console.error(`Failed to create template for ${item.language}:`, error);
            }
          }
        }

        if (this.templatesCreatedCount > 0) {
          toastr.success(`${this.templatesCreatedCount} template(s) created successfully`);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toastr.error("Failed to create any templates");
        }
      } catch (error) {
        console.error("Error creating templates:", error);
        toastr.error("Failed to create templates: " + (error.message || "Unknown error"));
      } finally {
        this.isCreatingTemplates = false;
      }
    },

    async downloadImage(imageUrl, label = "image") {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const safeLabel = label ? label.replace(/\s+/g, "_") : "image";
        a.href = url;
        a.download = `${safeLabel}-${timestamp}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
        toastr.error("Failed to download image");
      }
    },

    async downloadAll() {
      for (const result of this.translationResults) {
        for (const langItem of result.languages) {
          if (langItem.url && !langItem.isLoading && !langItem.error) {
            await this.downloadImage(langItem.url, `${result.name || result.instanceId}-${langItem.language}`);
            // Small delay between downloads to avoid browser blocking
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modal-content-wrapper {
  min-height: 200px;
}

.template-selection-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.template-selection-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8f9fa;
  }

  &.selected {
    background-color: #e8f4fd;
  }
}

.template-thumb {
  width: 60px;
  height: 40px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 3px;
    border: 1px solid #dee2e6;
  }
}

.template-thumb-placeholder {
  width: 60px;
  height: 40px;
  flex-shrink: 0;
  background-color: #e9ecef;
  border-radius: 3px;
}

.template-name {
  font-weight: 500;
  font-size: 14px;
}

.template-image-config {
  background-color: #f8f9fa;
}

.image-checkbox-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.image-checkbox-item {
  display: flex;
  align-items: center;
}

.results-container {
  max-height: 500px;
  overflow-y: auto;
}

.translated-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.translated-image-item {
  text-align: left;
}

.translated-image-label {
  display: block;
  font-weight: 500;
  margin-bottom: 4px;
  color: #495057;
  font-size: 13px;
}

.modal-preview-image {
  max-height: 180px;
  max-width: 100%;
  display: block;
  border-radius: 4px;
}

.image-preview-wrapper {
  position: relative;
  display: inline-block;
}

.image-actions-container {
  display: none;
  position: absolute;
  top: 8px;
  right: 8px;
  gap: 6px;
  z-index: 1;
  flex-direction: row;
  align-items: center;
}

.image-preview-wrapper:hover .image-actions-container {
  display: flex;
}

.download-btn,
.retry-btn {
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  i {
    font-size: 1rem;
    line-height: 1;
  }
}

.image-thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.image-thumbnail-placeholder {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 4px;
  backdrop-filter: blur(2px);
}

::v-deep .image-dropdown-menu {
  max-height: 300px;
  overflow-y: auto;

  .dropdown-item {
    cursor: pointer;
    padding: 8px 16px;

    &:hover,
    &:focus {
      background-color: #f8f9fa;
    }

    &.active {
      background-color: #e9ecef;
      color: #212529;
    }
  }
}
</style>
